import type {ClientSitePrefs} from '../context/site-preferences'
import type {TpaConfigContextType} from 'hec/context/tpa-config'
export type {TpaConfigContextType}
import type {Request} from 'express'

/**
 * Helper function to extract cookie value
 */
const getCookieValue = (cookieString: string, name: string) => {
    const nameEQ = name + '='
    return (
        cookieString
            .split(';')
            .map((c) => c.trim())
            .find((c) => c.startsWith(nameEQ))
            ?.substring(nameEQ.length) || null
    )
}

export const getTpaContextFromRequest = (req: Request) => {
    console.log('Request url in getTpaContextFromRequest:', req.url) // Debugging line

    const parsedUrl =
        req?.url && typeof req.url === 'string'
            ? new URL(req.url.includes('http') ? req.url : `http://localhost:3000${req.url}`)
            : null
    console.log('Parsed URL:', parsedUrl ? parsedUrl.href : 'No valid URL') // Debugging line
    const searchParams = new URLSearchParams(parsedUrl?.search || req.url)
    const afidFromUrl = searchParams.get('afid') || searchParams.get('AFID')
    if (afidFromUrl) {
        return {
            afid: afidFromUrl,
            hasContext: true
        }
    }

    const cookieHeader = req.headers.cookie || ''
    const tpaBrandingCookie = getCookieValue(cookieHeader, 'tpaBranding')
    const afidFromTpaBranding = tpaBrandingCookie ? tpaBrandingCookie.split('|')[0] : null

    return {
        afid: afidFromTpaBranding,
        hasContext: !!afidFromTpaBranding
    }
}

/**
 * Fetch site preferences and compute TPA suppression logic server-side
 * This eliminates the need for client-side API calls to sensitive endpoints
 */
const computeTpaSuppressionSSR = (req: Request, sitePreferencesData: ClientSitePrefs) => {
    const {afid, hasContext} = getTpaContextFromRequest(req)
    if (!hasContext || !afid) {
        return {
            shouldSuppressContent: false,
            currentAfid: null,
            isLoading: false,
            allowedAfids: null
        }
    }

    const allowedAfids = sitePreferencesData.LMN.lmnListAllowsTPA

    if (!allowedAfids) {
        return {
            shouldSuppressContent: hasContext,
            currentAfid: afid,
            isLoading: false,
            allowedAfids: null
        }
    }

    const isAllowed = allowedAfids
        .split(',')
        .map((s) => s.trim())
        .includes(afid)
    return {
        shouldSuppressContent: !isAllowed,
        currentAfid: afid,
        isLoading: false,
        allowedAfids
    }
}

const getTpaBalanceConfigSSR = (req: Request, sitePreferencesData: ClientSitePrefs) => {
    const balanceTrackerExpiryDays = sitePreferencesData.TPA.balanceTrackerExpiryDays

    return {balanceTrackerExpiryDays: balanceTrackerExpiryDays || 60}
}

export const getTPAContextSSR = (
    req: Request,
    sitePreferencesData: ClientSitePrefs
): TpaConfigContextType => {
    return {
        suppressionData: computeTpaSuppressionSSR(req, sitePreferencesData),
        balanceConfig: getTpaBalanceConfigSSR(req, sitePreferencesData)
    }
}
